export type ReleaseKind = 'app' | 'ocrl' | 'launcher';
export type ReleaseStatus = 'uploading' | 'ready' | 'revoked';
export type RolloutStatus = 'active' | 'paused' | 'cancelled' | 'completed';
export type PauseReason = 'rolled_back' | 'crash_loop' | 'failed' | 'manual';
export type InstallState =
  | 'pending' | 'downloading' | 'downloaded' | 'waiting_for_idle' | 'installing' | 'probation'
  | 'installed' | 'failed' | 'rolled_back' | 'cancelled';
export type WaitingReason = 'not_before' | 'busy' | 'settlement' | 'money' | 'offline' | 'window' | 'reader' | 'disk_space' | 'settings_invalid';

export const RELEASE_KINDS: ReleaseKind[] = ['app', 'ocrl', 'launcher'];
export const RELEASE_STATUSES: ReleaseStatus[] = ['uploading', 'ready', 'revoked'];
export const ROLLOUT_STATUSES: RolloutStatus[] = ['active', 'paused', 'cancelled', 'completed'];
export const PAUSE_REASONS: PauseReason[] = ['rolled_back', 'crash_loop', 'failed', 'manual'];
export const INSTALL_STATES: InstallState[] = ['pending', 'downloading', 'downloaded', 'waiting_for_idle', 'installing', 'probation', 'installed', 'failed', 'rolled_back', 'cancelled'];
export const WAITING_REASONS: WaitingReason[] = ['not_before', 'busy', 'settlement', 'money', 'offline', 'window', 'reader', 'disk_space', 'settings_invalid'];
export const UPDATE_ERROR_CODES = [
  'signature_invalid', 'key_unknown', 'manifest_invalid', 'package_invalid', 'environment_not_allowed', 'bench_build', 'downgrade',
  'launcher_too_old', 'hash_mismatch', 'size_mismatch', 'disk_full', 'extract_failed', 'file_hash_mismatch',
  'stray_ocrl_file', 'self_test_failed', 'power_or_stop', 'probation_crash', 'probation_timeout', 'probation_hung',
  'check_database', 'check_settings', 'check_first_frame', 'check_reader', 'check_ocrl_version', 'heartbeat_rejected',
  'launcher_probation_failed', 'launcher_reverted', 'integrity_failed', 'crash_loop',
] as const;
export type UpdateErrorCode = (typeof UPDATE_ERROR_CODES)[number];

export interface Release {
  id: string;
  kind: ReleaseKind;
  version: string;
  build: number | null;
  status: ReleaseStatus;
  gitCommit: string | null;
  keyId: string | null;
  environmentPolicy: string[] | null;
  minLauncherVersion: string | null;
  journalSchema: number | null;
  journalBreaking: boolean;
  octopusAffecting: boolean;
  ocrlVersion: string | null;
  oclReference: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  signatureVerifiedAt: string | null;
  packageVerifiedAt: string | null;
  uploadError: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
  revokeReason: string | null;
  activeRollouts: number;
  keyActive: boolean;
}

export interface RolloutProgress {
  targeted: number;
  installed: number;
  inProgress: number;
  waiting: number;
  failed: number;
  rolledBack: number;
  notOffered: number;
}

export type RolloutTargetType = 'all' | 'site' | 'kiosk' | 'group';

export interface Rollout {
  id: string;
  releaseId: string;
  targetType: RolloutTargetType;
  targetId: string | null;
  targetName: string | null;
  channel: 'pilot' | 'production';
  policy: 'one_at_a_time_per_site' | 'all_at_once';
  siteConcurrency: number;
  notBefore: string | null;
  urgent: boolean;
  urgentReason: string | null;
  status: RolloutStatus;
  pausedAt: string | null;
  pauseReason: PauseReason | null;
  pausedBy: string | null;
  pausedByEmail: string | null;
  createdBy: string | null;
  createdAt: string;
  cancelledAt: string | null;
  completedAt: string | null;
  progress: RolloutProgress;
}

export interface Install {
  id: string;
  kioskId: string;
  kioskName: string;
  siteId: string;
  siteName: string;
  releaseId: string;
  rolloutId: string;
  state: InstallState;
  waitingReason: WaitingReason | null;
  blockedReason: 'launcher_outdated' | null;
  attempts: number;
  generation: number;
  firstOfferedAt: string | null;
  downloadedAt: string | null;
  installedAt: string | null;
  rolledBackAt: string | null;
  failedAt: string | null;
  lastErrorCode: string | null;
  lastError: string | null;
  updatedAt: string;
  lastHeartbeatAt: string | null;
}

export interface ReleaseDetail extends Release {
  manifestFiles: number | null;
  rollouts: Rollout[];
  installs: Install[];
}

export interface UploadLink {
  uploadUrl: string;
  path: string;
  expiresAt: string;
}

export interface KioskGroup {
  id: string;
  name: string;
  description: string | null;
  isTestGroup: boolean;
  memberCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KioskGroupMember {
  kioskId: string;
  kioskName: string;
  siteId: string;
  siteName: string;
  status: string;
  addedAt: string;
}

export interface KioskGroupDetail extends KioskGroup {
  members: KioskGroupMember[];
}
